import {
  ChangeDetectorRef,
  Component,
  OnInit,
  QueryList,
  ViewChildren,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatNativeDateModule } from "@angular/material/core";
import { MatRadioModule } from "@angular/material/radio";
import { MatTooltipModule } from "@angular/material/tooltip";
import { CdkTextareaAutosize, TextFieldModule } from "@angular/cdk/text-field";

import { InventarioIAService } from "../inventario-ia.service";
import {
  CatAreaIA,
  CatCicloVida,
  CatNivelSupervision,
  CatTipoImpacto,
  InventarioIA,
  InventarioIAImpacto,
} from "src/app/shared/model/iso42001/inventario-ia.model";

type ModoPropietario = "AREA" | "EXTERNO";

@Component({
  selector: "app-modal-inventario-ia",
  standalone: true,
  templateUrl: "./modal-inventario-ia.component.html",
  styleUrls: ["./modal-inventario-ia.component.css"],
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatRadioModule,
    MatTooltipModule,
    TextFieldModule,
  ],
})
export class ModalInventarioIAComponent implements OnInit {
  inventario: InventarioIA = this.crearVacio();
  modoPropietario: ModoPropietario = "AREA";

  ciclosVida: CatCicloVida[] = [];
  nivelesSupervision: CatNivelSupervision[] = [];
  tiposImpacto: CatTipoImpacto[] = [];
  areas: CatAreaIA[] = [];

  /* Textareas de impacto (positivo/negativo) con autosize. Se fuerza el
     recálculo de su alto al cargar un activo, porque cdkTextareaAutosize no
     reajusta solo cuando el texto llega por código (edición). */
  @ViewChildren(CdkTextareaAutosize)
  private autosizesImpactos!: QueryList<CdkTextareaAutosize>;

  /* Memoria por renglón del texto de impacto según su tipo: al cambiar el tipo
     se guarda el texto del tipo anterior y se restaura el del nuevo (si ya lo
     tuvo), para no perder nada si el usuario regresa al tipo previo. Es un
     WeakMap (por objeto), así que no se serializa ni viaja al backend. */
  private memoriaImpacto = new WeakMap<
    InventarioIAImpacto,
    { previo: number | null; textos: Record<number, { pos: string; neg: string }> }
  >();

  /* Listas agrupadas por sección. Se calculan UNA vez al cargar el catálogo /
     el activo (no en getters), para no recrear las opciones del mat-select en
     cada ciclo de detección de cambios. */
  tiposPotenciales: CatTipoImpacto[] = [];
  tiposSociales: CatTipoImpacto[] = [];
  tiposSinClasificar: CatTipoImpacto[] = [];
  tiposHistoricos: CatTipoImpacto[] = [];

  esEdicion = false;
  guardando = false;
  error = "";

  constructor(
    private service: InventarioIAService,
    private route: ActivatedRoute,
    private router: Router,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get("id");
    this.esEdicion = !!id;

    this.cargarCatalogos(() => {
      if (this.esEdicion) {
        this.cargarInventario(Number(id));
      }
    });
  }

  cargarCatalogos(despues?: () => void): void {
    let pendientes = 4;
    const done = () => {
      pendientes -= 1;
      if (pendientes === 0 && despues) {
        despues();
      }
      this.cdr.detectChanges();
    };

    this.service.ciclosVida().subscribe({
      next: (res) => {
        this.ciclosVida = res.ciclosVida || [];
        done();
      },
      error: () => done(),
    });
    this.service.nivelesSupervision().subscribe({
      next: (res) => {
        this.nivelesSupervision = res.nivelesSupervision || [];
        done();
      },
      error: () => done(),
    });
    this.service.tiposImpacto().subscribe({
      next: (res) => {
        this.tiposImpacto = res.tiposImpacto || [];
        this.clasificarTipos();
        done();
      },
      error: () => done(),
    });
    this.service.catAreas().subscribe({
      next: (res) => {
        this.areas = res.areas || [];
        done();
      },
      error: () => done(),
    });
  }

  cargarInventario(id: number): void {
    this.service.obtener(id).subscribe((res) => {
      if (res.inventario) {
        this.inventario = res.inventario;
        this.modoPropietario = this.inventario.idArea ? "AREA" : "EXTERNO";
        if (!this.inventario.impactos) {
          this.inventario.impactos = [];
        }
        if (!this.inventario.idAreasUsuarias) {
          this.inventario.idAreasUsuarias = [];
        }
        this.inventario.impactos.forEach((imp) => this.initMemoriaImpacto(imp));
        this.recalcularHistoricos();
        this.cdr.detectChanges();
        this.reajustarImpactos();
      }
    });
  }

  agregarImpacto(): void {
    if (!this.inventario.impactos) {
      this.inventario.impactos = [];
    }
    const nuevo: InventarioIAImpacto = {
      idTipoImpacto: null,
      descripcionPositivo: "",
      descripcionNegativo: "",
    };
    this.inventario.impactos.push(nuevo);
    this.initMemoriaImpacto(nuevo);
    this.recalcularHistoricos();
    this.reajustarImpactos();
  }

  eliminarImpacto(idx: number): void {
    this.inventario.impactos?.splice(idx, 1);
    this.recalcularHistoricos();
  }

  /** Prepara la memoria de un renglón de impacto (al cargar o al agregar). */
  private initMemoriaImpacto(imp: InventarioIAImpacto): void {
    this.memoriaImpacto.set(imp, {
      previo: imp.idTipoImpacto ?? null,
      textos: {},
    });
  }

  /**
   * Al CAMBIAR el tipo de un renglón: guarda el texto del tipo anterior y
   * restaura el del nuevo tipo (si ya lo tenía); si es un tipo nuevo, deja
   * positivo/negativo en blanco. Solo se dispara por interacción del usuario
   * (selectionChange), NO al cargar el activo. Así nada se pierde: si el
   * usuario regresa al tipo previo, su texto vuelve a aparecer.
   */
  onTipoImpactoChange(imp: InventarioIAImpacto): void {
    let mem = this.memoriaImpacto.get(imp);
    if (!mem) {
      mem = { previo: null, textos: {} };
      this.memoriaImpacto.set(imp, mem);
    }
    const nuevo = imp.idTipoImpacto ?? null;
    if (mem.previo === nuevo) {
      return;
    }
    // 1) Guarda el texto actual bajo el tipo que se está dejando.
    if (mem.previo != null) {
      mem.textos[mem.previo] = {
        pos: imp.descripcionPositivo || "",
        neg: imp.descripcionNegativo || "",
      };
    }
    // 2) Restaura el texto del nuevo tipo (o blanco si nunca tuvo).
    const guardado = nuevo != null ? mem.textos[nuevo] : undefined;
    imp.descripcionPositivo = guardado ? guardado.pos : "";
    imp.descripcionNegativo = guardado ? guardado.neg : "";
    // 3) Recuerda el tipo actual para el próximo cambio.
    mem.previo = nuevo;
    this.reajustarImpactos();
  }

  /**
   * ¿El tipo de impacto ya está usado en OTRO renglón? Sirve para deshabilitar
   * la opción en los demás selects y no permitir tipos repetidos. El renglón
   * que ya tiene ese tipo seleccionado no se bloquea a sí mismo.
   */
  tipoBloqueado(
    idTipo: number | null | undefined,
    actual: InventarioIAImpacto,
  ): boolean {
    if (idTipo == null) {
      return false;
    }
    return (this.inventario.impactos || []).some(
      (o) => o !== actual && o.idTipoImpacto === idTipo,
    );
  }

  /**
   * Fuerza a los textareas de impacto (positivo/negativo) a recalcular su alto
   * para mostrar TODO el texto. Necesario al cargar un activo: el contenido se
   * asigna por código y cdkTextareaAutosize no reajusta solo en ese caso.
   */
  private reajustarImpactos(): void {
    setTimeout(() => {
      this.autosizesImpactos?.forEach((a) => a.resizeToFitContent(true));
    });
  }

  /** Reparte el catálogo activo en las dos secciones ISO (+ grupo "Otros"). */
  private clasificarTipos(): void {
    const clasificadas = ["POTENCIAL", "SOCIAL", "AMBAS"];
    this.tiposPotenciales = this.tiposImpacto.filter(
      (t) => t.seccion === "POTENCIAL" || t.seccion === "AMBAS",
    );
    this.tiposSociales = this.tiposImpacto.filter(
      (t) => t.seccion === "SOCIAL" || t.seccion === "AMBAS",
    );
    this.tiposSinClasificar = this.tiposImpacto.filter(
      (t) => !clasificadas.includes(t.seccion || ""),
    );
    this.recalcularHistoricos();
  }

  /**
   * Tipos "históricos": los que un activo ya tiene capturados pero que ya no
   * están en el catálogo activo. Se sintetizan del nombre que viaja con el
   * impacto para que el select los siga mostrando sin perder el dato.
   */
  private recalcularHistoricos(): void {
    const activos = new Set(this.tiposImpacto.map((t) => t.idTipoImpacto));
    const vistos = new Map<number, string>();
    (this.inventario.impactos || []).forEach((imp) => {
      if (imp.idTipoImpacto != null && !activos.has(imp.idTipoImpacto)) {
        vistos.set(imp.idTipoImpacto, imp.tipoImpactoNombre || "Tipo histórico");
      }
    });
    this.tiposHistoricos = Array.from(vistos, ([idTipoImpacto, nombre]) => ({
      idTipoImpacto,
      nombre,
      seccion: "HISTORICO",
    }));
  }

  /** trackBy para que *ngFor no recree las opciones del mat-select. */
  trackTipo(_index: number, tipo: CatTipoImpacto): number | null {
    return tipo.idTipoImpacto ?? null;
  }

  cambiarModoPropietario(): void {
    if (this.modoPropietario === "AREA") {
      this.inventario.propietarioExterno = "";
    } else {
      this.inventario.idArea = null;
    }
  }

  guardar(): void {
    this.error = "";
    if (!this.validar()) {
      return;
    }
    this.guardando = true;

    const obs = this.esEdicion
      ? this.service.actualizar(this.inventario)
      : this.service.guardar(this.inventario);

    obs.subscribe({
      next: (res) => {
        if (res.codigoError) {
          this.error = res.mensajeError || "Error al guardar";
          this.guardando = false;
          this.cdr.detectChanges();
          return;
        }
        this.router.navigate(["/admin/inventario-ia/gestion"]);
      },
      error: (e) => {
        this.error = e?.error?.mensajeError || "Error al guardar inventario.";
        this.guardando = false;
        this.cdr.detectChanges();
      },
    });
  }

  cancelar(): void {
    this.router.navigate(["/admin/inventario-ia/gestion"]);
  }

  private validar(): boolean {
    if (!this.inventario.nombreIa?.trim()) {
      this.error = "El nombre del sistema de IA es obligatorio.";
      return false;
    }
    if (this.modoPropietario === "AREA" && !this.inventario.idArea) {
      this.error = "Selecciona el área propietaria.";
      return false;
    }
    if (
      this.modoPropietario === "EXTERNO" &&
      !this.inventario.propietarioExterno?.trim()
    ) {
      this.error = "Indica el nombre del propietario externo.";
      return false;
    }
    return true;
  }

  private crearVacio(): InventarioIA {
    return {
      nombreIa: "",
      version: "",
      proposito: "",
      usoPrevisto: "",
      idArea: null,
      propietarioExterno: "",
      idCicloVida: null,
      ubicacion: "",
      componentesSistema: "",
      componentesDatos: "",
      herramientasIa: "",
      sistemasInformaticos: "",
      recursosHumanos: "",
      procesosIntervencion: "",
      idNivelSupervision: null,
      estatus: "ACTIVO",
      impactos: [],
      idAreasUsuarias: [],
    };
  }
}
